import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Heading from 'LuxKit/Typography/Heading'
import { EmptyArray, last } from 'lib/array/arrayUtils'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import JourneyV2DetailsModalFlight from './JourneyV2DetailsModalFlight'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineClockFiveIcon from 'LuxKit/Icons/line/LineClockFiveIcon'
import { AMADEUS, JETSTAR, TRAVEL_FUSION } from 'constants/provider'
import styled from 'styled-components'
import { rem } from 'polished'
import FlightFareRulesModalContent from 'components/Common/Modals/FlightFareRulesModal/FlightFareRulesModalContent'
import moment from 'moment'
import { isLegFlight } from 'lib/flights/flightUtils'
import Sheet from 'components/utils/Sheet'
import CheckboxInput from 'LuxKit/Checkbox/CheckboxInput'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import TextLink from 'LuxKit/TextLink'
import { useIntl, FormattedMessage } from 'react-intl'

const SlideModalContent = styled.div`
  overflow-x: hidden;
`
const SlidePanels = styled.div`
  display: grid;
  height: 100%;
  grid-template-columns: 100% 100%;
  transform: translateX(0);
  transition: transform 0.3s;
  gap: ${rem(20)};

  >:first-child {
    transition: opacity 0.3s;
    opacity: 1;
  }

  >:nth-child(2) {
    transition: opacity 0.3s;
    opacity: 0;
  }

  &.right {
    transform: translateX(calc(-100% - ${rem(20)}));

    >:first-child {
      opacity: 0;
    }

    >:nth-child(2) {
      opacity: 1;
    }
  }
`

const SlidePanel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`

interface Props {
  journey?: App.JourneyV2
  leg?: App.Leg
  legIndex?: number
  fareFamily?: App.FlightFareFamily
  inclusions?: App.InclusionFeatures
  addedBaggage?: App.InclusionFeatures
  isStandaloneFlights?: boolean
  reservationInfo?: App.NDCReservationInfo
  pnrId?: string
  region?: string
  isStandalone?: boolean
  isDeparting?: boolean
  fareRulesURL: string | undefined
  fareRulesParams: any | undefined
  setFareRulesURL: (url: string) => void
  setFareRulesParams: (params: any) => void
  onShowCoverageUnforeseenEvents?: (e: React.MouseEvent) => void
  showRefundCheckbox?: boolean
  onToggleEmbedInsurance?: () => void
  areAllFlightsCovered?: boolean
  quoteTotal?: number
  withInsurance?: boolean
}

function JourneyV2Details(props: Props) {
  const {
    journey,
    leg,
    legIndex,
    fareFamily,
    inclusions,
    addedBaggage,
    reservationInfo,
    pnrId,
    region,
    isStandalone,
    fareRulesURL,
    fareRulesParams,
    setFareRulesURL,
    setFareRulesParams,
    onShowCoverageUnforeseenEvents,
    showRefundCheckbox,
    onToggleEmbedInsurance,
    areAllFlightsCovered,
    quoteTotal,
    withInsurance,
  } = props

  const intl = useIntl()

  let allFlights: Array<App.LegFlight | App.FlightV2> = EmptyArray
  let firstFlight: App.LegFlight | App.FlightV2 | undefined
  let lastFlight: App.LegFlight | App.FlightV2 | undefined
  let provider: string | undefined
  if (leg) {
    allFlights = leg.flights
    firstFlight = allFlights[0]
    lastFlight = last(allFlights)
    provider = leg.provider
  } else if (journey) {
    allFlights = journey.flightGroup.flights
    firstFlight = allFlights[0]
    lastFlight = last(allFlights)
    provider = journey.provider
  }

  const stopovers = useMemo(() => {
    const stopovers: Record<string, { hours: number, minutes: number }> = {}
    if (allFlights.length > 1) {
      for (let i = 1; i < allFlights.length; i++) {
        const flight = allFlights[i]
        const previousFlight = allFlights[i - 1]
        const flightDateTime = `${flight.departingDate} ${flight.departingTime}`
        const previousFlightDateTime = `${previousFlight.arrivalDate} ${previousFlight.arrivalTime}`
        const stopoverDiff = moment(flightDateTime).diff(moment(previousFlightDateTime), 'minutes')
        stopovers[i] = {
          hours: Math.floor(stopoverDiff / 60),
          minutes: stopoverDiff % 60,
        }
      }
    }
    return stopovers
  }, [allFlights])

  const amadeusFareRulesParams = useCallback(() => {
    let fareRuleParams: any = {
      provider,
      pnrId,
      region,
      isBundledWithAccommodation: !isStandalone,
    }
    if (reservationInfo?.kind === 'amadeus-ndc') {
      fareRuleParams = {
        ...fareRuleParams,
        bookingInfo: {
          carrier: reservationInfo.carrier,
          kind: reservationInfo.kind,
          orderId: reservationInfo.orderId,
        },
        legIndex,
      }
    }
    return fareRuleParams
  }, [isStandalone, pnrId, provider, region, reservationInfo, legIndex])

  const onShowFareRules = useCallback((fareRuleUrl: string) => {
    let fareRuleParams

    switch (provider) {
      case TRAVEL_FUSION:
        fareRuleParams = {
          provider,
          pnrId,
          region,
        }
        break
      case JETSTAR:
        fareRuleParams = {
          provider,
        }
        break
      case AMADEUS:
        fareRuleParams = amadeusFareRulesParams()
        break
      default:
        fareRuleParams = null
    }

    setFareRulesURL(fareRuleUrl)
    setFareRulesParams(fareRuleParams)
  }, [amadeusFareRulesParams, pnrId, provider, region, setFareRulesParams, setFareRulesURL])

  const onClickCoverageUnforeseenEvents = (e: React.MouseEvent) => {
    e.stopPropagation()
    onShowCoverageUnforeseenEvents?.(e)
  }

  return <SlideModalContent>
    <SlidePanels className={fareRulesURL ? 'right' : 'left'}>
      <SlidePanel>
        <VerticalSpacer gap={12}>
          {firstFlight && lastFlight && <Heading variant="heading6">
            {intl.formatMessage({
              id: 'k0YJYWB0UhTuPqO4kNS6',
              defaultMessage: '{departureAirport} ({departureCode}) to {arrivalAirport} ({arrivalCode})',
              description: 'Heading showing flight route in details view in the flights flow',
            }, {
              departureAirport: firstFlight.departureAirportName,
              departureCode: firstFlight.departureAirport,
              arrivalAirport: lastFlight.arrivalAirportName,
              arrivalCode: lastFlight.arrivalAirport,
            })}
          </Heading>}
          <VerticalSpacer gap={8}>
            {allFlights.map((flight, index) => <React.Fragment key={isLegFlight(flight) ? flight.flightUniqueId : flight.id}>
              {index in stopovers && <MessageBanner icon={<LineClockFiveIcon />} kind="warning">
                {intl.formatMessage({
                  id: 'im2eAqvC2ssKq2BCncma',
                  defaultMessage: '{hours}h {minutes}m layover in {airportName} ({airportCode})',
                  description: 'Stopover duration and location in flight details in the flights section',
                }, {
                  hours: stopovers[index].hours,
                  minutes: stopovers[index].minutes,
                  airportName: flight.departureAirportName,
                  airportCode: flight.departureAirport,
                })}
              </MessageBanner>}
              <JourneyV2DetailsModalFlight
                key={isLegFlight(flight) ? flight.flightUniqueId : flight.id}
                journey={journey || leg!}
                flight={flight}
                flightIndex={index}
                fareFamily={fareFamily}
                inclusions={inclusions}
                addedBaggage={addedBaggage}
                defaultOpen={allFlights.length === 1}
                enableViewFareRulesButton={!!pnrId}
                onShowFareRules={onShowFareRules}
                withInsurance={withInsurance}
              />
            </React.Fragment>)}
          </VerticalSpacer>
          {showRefundCheckbox && quoteTotal && <Sheet background="neutral-eight" borderRadius="S" border="neutral-five" borderWidth={1} padding="24 16">
            <CheckboxInput size="medium" checked={areAllFlightsCovered} onChange={onToggleEmbedInsurance}>
              <Group direction="horizontal" gap={4} verticalAlign="start" wrap="wrap">
                <BodyText variant="medium" as="span">
                  <FormattedMessage
                    id="LNTBTaoItr0vGzlTRwgs"
                    defaultMessage="<highlight>100% refund available</highlight> from <bold>{price}</bold> for <eventsLink>unforeseen events</eventsLink>"
                    description="Insurance refund offer with price and link to unforeseen events coverage"
                    values={{
                      highlight: (chunks) => <BodyText variant="medium" weight="bold" colour="highlight-secondary" as="span">{chunks}</BodyText>,
                      bold: (chunks) => <BodyText variant="medium" weight="bold" colour="neutral-one" as="span">{chunks}</BodyText>,
                      price: <FormatCurrency value={quoteTotal} format="roundedDollar" signDisplay="always" />,
                      eventsLink: (chunks) => <TextLink size="medium" weight="regular" underline colour="neutral-one" onClick={onClickCoverageUnforeseenEvents}>{chunks}</TextLink>,
                    }}
                  />
                </BodyText>
              </Group>
            </CheckboxInput>
          </Sheet>}
        </VerticalSpacer>
      </SlidePanel>
      <SlidePanel>
        <FlightFareRulesModalContent
          urls={fareRulesURL ? [fareRulesURL] : undefined}
          params={fareRulesParams}
        />
      </SlidePanel>
    </SlidePanels>
  </SlideModalContent>
}

export default JourneyV2Details
